import React, { useEffect, useState, useMemo } from "react";
import { useHistory, Route, Switch, useRouteMatch } from "react-router-dom";
import { useFlowConfig } from "flows/hooks";
import Header from "flows/components/Header";
import {
  getFinalConfig,
  FLOW_STATE,
  useSessionCtx,
  getTransactionDetailsFromFlowConfig,
} from "utils";
import { IframeOrFullPageWrapper } from "components/Container";
import { AddPaymentMethodContent } from "components/AddPaymentMethod";
import { PAYMENT_SELECTION } from "flows/constants";
import SelectPaymentMethods from "./components/SelectPaymentMethods";
import { FraudCheck } from "components/FraudCheck";
import { useIntl } from "react-intl";

const defaultChooserConfig = {
  chooser: {
    include: [],
    exclude: [],
    addCC: true,
    addPaypal: true,
  },
};

/**
 * PaymentSelectionFlow
 * @description
 * Payment Selection Flow
 * @param {object} props
 * @param {object} props.config
 * @param {OnReady} props.onReady
 * @param {OnComplete} props.onComplete
 */
export default function PaymentSelectionFlow({ config = {}, onReady, onComplete }) {
  useEffect(onReady, [onReady]);

  const { hideExitButtons } = useFlowConfig();
  const [viewedAddPM, setViewedAddPM] = useState(false);

  const history = useHistory();
  const { path } = useRouteMatch();

  const { sessionCtx } = useSessionCtx();
  const { flowConfig } = sessionCtx;

  const { formatMessage } = useIntl();

  // details about transaction in {transaction_amount, currency} format
  const transactionDetails = getTransactionDetailsFromFlowConfig(flowConfig) || {};

  // final chooser config considering default and in settings
  const { chooser: chooserConfig } = useMemo(
    () => getFinalConfig(PAYMENT_SELECTION, defaultChooserConfig, config),
    [config]
  );

  // map though usedWallets and pull the exact amount to use
  // non-wallet payment method gets amount 0
  const handleSubmit = (values) => {
    let paymentMethods = values.usedWallets.map((wallet) => ({
      payment_method_id: wallet.id,
      amount: wallet.amountUsed,
    }));
    if (values.usedPaymentMethod) {
      paymentMethods.push({ payment_method_id: values.usedPaymentMethod.id, amount: 0 });
    }

    onComplete({
      state: FLOW_STATE.COMPLETE,
      paymentMethods,
    });
  };

  const handleCloseButtonClicked = () => {
    onComplete({ state: FLOW_STATE.CANCEL });
  };

  const onAddPMOpen = () => {
    history.push(`${path}/addpm`);
    if (!viewedAddPM) {
      setViewedAddPM(true);
    }
  };

  const onAddPMClose = () => {
    history.push(path);
  };

  return (
    <IframeOrFullPageWrapper includeTosAndPrivacy withoutPadding>
      <Header
        title={formatMessage({
          defaultMessage: "Payment Method Selection",
          description: "Header title",
        })}
        showLanguage={true}
        onCloseButtonClick={!hideExitButtons ? handleCloseButtonClicked : undefined}
      />
      <Switch>
        <Route exact path={`${path}/`}>
          <SelectPaymentMethods
            chooserConfig={chooserConfig}
            transactionDetails={transactionDetails}
            enableAutoRedirect={!viewedAddPM}
            onSubmit={handleSubmit}
            onAddPMOpen={onAddPMOpen}
            onAddPMClose={onAddPMClose}
          />
        </Route>
        <Route path={`${path}/addpm`}>
          <>
            <FraudCheck isAsync />
            <AddPaymentMethodContent
              onComplete={onAddPMClose}
              onCancel={onAddPMClose}
              chooserConfig={chooserConfig}
              description={formatMessage({
                defaultMessage:
                  "To proceed with payment selection, please begin by adding a payment method.",
                description: "Sub header",
              })}
            />
          </>
        </Route>
      </Switch>
    </IframeOrFullPageWrapper>
  );
}
